import { ChevronDown } from 'lucide-react';
import { useMemo } from 'react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';
import { useTeamsInfo } from '../admin-hooks.js';

/**
 * Shared Teams selector. Mirrors the legacy `wf-audit` top-right
 * Teams dropdown that the operator confirmed lives at the corner
 * of every Fusion section page (Scenarios, Daily Dashboard, Value,
 * etc).
 *
 * The component is stateless wrt routing; the parent threads the
 * URL `?team=` param in and out. `TEAM_ALL` (`'__all__'`) is the
 * sentinel for "no team filter".
 *
 * Today this is the canonical entry-point to the team filter; the
 * folder picker stays inline next to the page title where it
 * belongs. Workspace dropdowns were intentionally removed from
 * Fusion pages — operator-confirmed in PR-A.
 */
export const TEAM_ALL = '__all__';

export interface TeamsDropdownProps {
  /** Currently-selected team (Fusion id) or `TEAM_ALL`. */
  team: string;
  onTeamChange: (team: string) => void;
  /** Optional label override for the trigger when no team is
   *  selected (defaults to "All teams"). */
  allLabel?: string;
}

export function TeamsDropdown({
  team,
  onTeamChange,
  allLabel = 'All teams',
}: TeamsDropdownProps): JSX.Element {
  const { data: rows = [] } = useTeamsInfo();

  const teamOptions = useMemo(
    () =>
      rows.flatMap((r) => {
        if (!r || typeof r !== 'object') return [];
        const fusionId = typeof r.fusionId === 'string' ? r.fusionId : null;
        const name = typeof r.name === 'string' ? r.name : null;
        if (!fusionId || !name) return [];
        return [{ fusionId, name }];
      }),
    [rows],
  );

  const teamLabel =
    team === TEAM_ALL
      ? allLabel
      : (teamOptions.find((t) => t.fusionId === team)?.name ?? `Team ${team}`);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border border-foreground/15 px-2.5 py-1 text-sm text-foreground/80 transition-colors hover:border-foreground/40 hover:text-foreground"
        >
          <span className="max-w-[180px] truncate">{teamLabel}</span>
          <ChevronDown className="h-4 w-4 text-foreground/50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[60vh] min-w-[220px] overflow-y-auto">
        <DropdownMenuLabel>Team</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={team === TEAM_ALL}
          onCheckedChange={() => onTeamChange(TEAM_ALL)}
        >
          {allLabel}
        </DropdownMenuCheckboxItem>
        {teamOptions.length > 0 ? <DropdownMenuSeparator /> : null}
        {teamOptions.map((t) => (
          <DropdownMenuCheckboxItem
            key={t.fusionId}
            checked={team === t.fusionId}
            onCheckedChange={() => onTeamChange(t.fusionId)}
          >
            {t.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
